import React, { useCallback } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import {
	Button,
	CloseButton,
	HookSelectFormGroup,
	OnlineLink,
	Skeleton,
} from "components";
import { DebounceTimeout } from "globalConstants";
import { useLoader } from "hooks";
import {
	useGetFeeTemplatesQuery,
	useUpdateFeeTemplateDefaultMutation,
} from "queries";
import { required } from "validators/validation";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./reassignModal.module.scss";

type ReassignDefaultFeeTemplateModalProps = {
	show: boolean;
	onHide: () => void;
	companyId: string;
	templateId: string;
	onSuccess?: Function;
	isOpenPageInNewTab?: boolean;
};

const ReassignDefaultFeeTemplateModal = ({
	show = false,
	onHide,
	companyId,
	templateId,
	onSuccess,
	isOpenPageInNewTab = false,
}: ReassignDefaultFeeTemplateModalProps) => {
	const formMethods = useForm({
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: { template: null },
	});

	const {
		handleSubmit,
		formState: { isDirty },
	} = formMethods;

	const { data: templates, isFetching: isLoadingTemplates } =
		useGetFeeTemplatesQuery({ companyId, isActive: true });
	const { mutateAsync: setDefault } = useUpdateFeeTemplateDefaultMutation();

	const templatesExceptionCurrent = templates?.filter(
		({ id }) => id !== templateId,
	);
	const isEmptyTemplates = templatesExceptionCurrent?.length === 0;
	const addNewTemplateLink = `/system-setup/agent-office-setup/edit-agent-office/${companyId}/add-fee-template`;

	const Loader = useLoader(isLoadingTemplates);

	const handleSubmitForm = useCallback(
		async ({ template }) => {
			onHide();
			await setDefault({
				companyId,
				templateId: template.id,
				isDefault: true,
			});
			onSuccess?.();
		},
		[companyId, onHide, onSuccess, setDefault],
	);

	return (
		<Modal show={show} onHide={onHide} animation={false} centered>
			<Loader
				skeleton={<Skeleton height={200} />}
				component={
					<FormProvider {...formMethods}>
						<form onSubmit={handleSubmit(handleSubmitForm)}>
							<Modal.Header className="pb-0">
								<Modal.Title as="h6" className="text-center">
									{isEmptyTemplates ? (
										<div className="text-warning">
											<WarningIcon width="30" />
										</div>
									) : (
										<div className="mb-3">
											Default Fee Template
										</div>
									)}
								</Modal.Title>
								<CloseButton onClick={onHide} />
							</Modal.Header>
							<Modal.Body>
								<div className={styles.content}>
									{isEmptyTemplates ? (
										<div className="text-center">
											There is only one Fee Template
											available.
											<br />
											Please create new Fee Template to
											deactivate existing.
										</div>
									) : (
										<>
											<div className="text-center mb-3">
												Please reassign default Fee
												Template:
											</div>
											<HookSelectFormGroup
												name="template"
												options={
													templatesExceptionCurrent
												}
												getOptionLabel={option =>
													option.name
												}
												getOptionValue={option =>
													option.id
												}
												placeholder="Please Select"
												isSearchable
												cacheOptions
												defaultOptions
												debounceTimeout={
													DebounceTimeout
												}
												rules={{
													validate: {
														required,
													},
												}}
												blurLoading={isLoadingTemplates}
												defaultValue={null}
												colLabel={{ sm: 12 }}
												colSelect={{ sm: 12 }}
												hideLabel
												brandingColor
												containerClassName={
													styles.ddlContainer
												}
											/>
										</>
									)}
								</div>
							</Modal.Body>
							<Modal.Footer className="justify-content-center pb-5">
								<Button
									variant="outline-primary"
									value="Cancel"
									onClick={onHide}
									className="mr-4"
								/>
								{isEmptyTemplates ? (
									<Button
										value="Add New"
										variant="primary"
										to={addNewTemplateLink}
										as={OnlineLink}
										onClick={onHide}
										target={
											isOpenPageInNewTab
												? "_blank"
												: "_self"
										}
									/>
								) : (
									<Button
										type="submit"
										value="Save"
										variant="primary"
										disabled={!isDirty}
									/>
								)}
							</Modal.Footer>
						</form>
					</FormProvider>
				}
			/>
		</Modal>
	);
};

export default ReassignDefaultFeeTemplateModal;
